/**
 * *Postmark has stopped sending to this address — it bounced on Oct 26. Check
 * it with the customer.* (Aaron, Oct 26; `docs/adapters/postmark.md`.)
 *
 * The warning the Inbox's composer (desktop and phone), New email and a
 * record's email draw when an address the email is about to go to is one
 * Postmark has stopped sending to — a hard bounce, a spam complaint, a manual
 * suppression. **It never stops the send**: Postmark refuses it itself, and
 * the row comes back *not sent* with Postmark's own reason; this is so nobody
 * is surprised. Asked of the server as the addresses change
 * (`GET /conversations/stopped-addresses`), only while the email goes from
 * Rowted — a person's own Outlook is not Postmark.
 */
import { useEffect, useMemo, useState } from 'react'
import { stoppedWords, type StoppedAddress } from '@rowted/shared'
import { cn } from '../../lib/cn.js'
import { activityRequest } from '../api.js'
import { Glyph } from '../glyphs.js'
import type { Copy } from '../new/address-box.js'

/** One answer per set of addresses for a minute — the composer asks again as each chip is added. */
const ASKED_MS = 60_000
const asked = new Map<string, { at: number; answer: Promise<StoppedAddress[]> }>()

/** For tests. */
export function forgetStopped(): void {
  asked.clear()
}

function ask(addresses: string[]): Promise<StoppedAddress[]> {
  const key = addresses.join(',')
  const known = asked.get(key)
  if (known && Date.now() - known.at < ASKED_MS) return known.answer
  const answer = activityRequest<{ items?: StoppedAddress[] }>(
    `/conversations/stopped-addresses?to=${encodeURIComponent(key)}`)
    .then((got) => (Array.isArray(got?.items) ? got.items : []))
    /* A warning that could not be asked for is no warning; the send still says what happened. */
    .catch(() => [])
  asked.set(key, { at: Date.now(), answer })
  return answer
}

/** The addresses an email is going to: who it is to, and the typed Cc and Bcc. */
export function addressesOf(to: string | null | undefined, ...boxes: readonly Copy[][]): string[] {
  const all = [to ?? '', ...boxes.flatMap((box) => box.flatMap((one) => (one.kind === 'address' ? [one.address] : [])))]
  return [...new Set(all.map((one) => one.trim().toLowerCase()).filter((one) => one.includes('@')))].sort()
}

/** The ones Postmark has stopped sending to, asked a beat after the addresses settle. */
export function useStoppedAddresses(addresses: string[], enabled: boolean): StoppedAddress[] {
  const key = enabled ? addresses.join(',') : ''
  const [found, setFound] = useState<{ key: string; items: StoppedAddress[] }>({ key: '', items: [] })
  useEffect(() => {
    if (!key) return
    let live = true
    const timer = setTimeout(() => {
      void ask(key.split(',')).then((items) => { if (live) setFound({ key, items }) })
    }, 250)
    return () => { live = false; clearTimeout(timer) }
  }, [key])
  return useMemo(() => (key && found.key === key ? found.items : []), [found, key])
}

/** The warning itself, one line per address, in the Inbox's amber band. */
export function StoppedWarning({
  stopped, to, className,
}: { stopped: StoppedAddress[]; to: string | null | undefined; className?: string }) {
  if (stopped.length === 0) return null
  const main = (to ?? '').trim().toLowerCase()
  return (
    <div role="status" data-stopped className={cn('flex flex-col gap-1', className)}>
      {stopped.map((one) => (
        <div key={one.address}
          className="flex items-start gap-[9px] rounded-[10px] border border-amber-band-line bg-amber-band-bg px-3 py-2
                     text-[12px] leading-[1.45] text-amber-band-text">
          <span className="mt-px inline-flex shrink-0"><Glyph name="info" size={14} /></span>
          <span>{stoppedWords(one, { isTo: one.address === main })}</span>
        </div>
      ))}
    </div>
  )
}
